import { useState, useEffect } from "react";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import { X, Heart, MapPin, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";

export default function Discover() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [matchPopup, setMatchPopup] = useState(null);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await fetch(`${API}/discover`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setProfiles(data);
      }
    } catch (error) {
      toast.error("Failed to load profiles");
    } finally {
      setLoading(false);
    }
  };

  const handleSwipe = async (action) => {
    if (currentIndex >= profiles.length) return;

    const targetUser = profiles[currentIndex];
    
    try {
      const response = await fetch(`${API}/swipe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          target_user_id: targetUser.user_id,
          action: action,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.match) {
          setMatchPopup({ ...data.matched_user, has_superlike: data.has_superlike });
        }
        if (action === "superlike") {
          toast.success("Super Like sent! ⭐", { duration: 2000 });
        }
      }
    } catch (error) {
      // Swipe failed - user sees toast
    }

    setCurrentIndex((prev) => prev + 1);
  };

  const currentProfile = profiles[currentIndex];

  return (
    <div className="min-h-screen bg-[#FFF5E1]">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          {loading ? (
            <div className="brutal-card h-[500px] flex items-center justify-center">
              <div className="animate-spin w-12 h-12 border-4 border-black border-t-transparent rounded-full"></div>
            </div>
          ) : profiles.length === 0 || currentIndex >= profiles.length ? (
            <div className="brutal-card text-center py-16" data-testid="no-profiles-card">
              <Sparkles className="w-16 h-16 mx-auto mb-4 text-[#FF66B2]" />
              <h2 className="text-2xl font-bold mb-2">No more profiles</h2>
              <p className="text-[#4A4A4A]">Check back later for new people!</p>
            </div>
          ) : (
            <>
              {/* Swipe Card Stack */}
              <div className="relative h-[500px] mb-8">
                <SwipeCard
                  key={currentProfile.user_id}
                  profile={currentProfile}
                  onSwipe={handleSwipe}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center gap-6">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleSwipe("pass")}
                  className="w-16 h-16 brutal-btn bg-white flex items-center justify-center rounded-full"
                  data-testid="pass-btn"
                >
                  <X className="w-8 h-8 text-[#FF4D4D]" />
                </motion.button>
                
                {/* Super Like Button */}
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 15 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleSwipe("superlike")}
                  className="w-14 h-14 brutal-btn bg-[#FFD700] flex items-center justify-center rounded-full"
                  data-testid="superlike-btn"
                >
                  <Star className="w-7 h-7 text-white" fill="white" />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleSwipe("like")}
                  className="w-16 h-16 brutal-btn bg-[#00CC99] flex items-center justify-center rounded-full"
                  data-testid="like-btn"
                >
                  <Heart className="w-8 h-8 text-white" />
                </motion.button>
              </div>
              
              {/* Legend */}
              <div className="flex justify-center gap-6 mt-4 text-sm text-[#4A4A4A]">
                <span>Pass</span>
                <span className="text-[#FFD700] font-bold">Super Like</span>
                <span>Like</span>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Match Popup */}
      <AnimatePresence>
        {matchPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setMatchPopup(null)}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className={`brutal-card text-white text-center max-w-sm ${
                matchPopup.has_superlike ? "bg-gradient-to-br from-[#FFD700] to-[#FF66B2]" : "bg-[#FF66B2]"
              }`}
              onClick={(e) => e.stopPropagation()}
              data-testid="match-popup"
            >
              <div className="match-animation">
                {matchPopup.has_superlike ? (
                  <div className="flex items-center justify-center gap-2">
                    <Star className="w-16 h-16" fill="white" />
                    <Heart className="w-20 h-20" fill="white" />
                    <Star className="w-16 h-16" fill="white" />
                  </div>
                ) : (
                  <Heart className="w-20 h-20 mx-auto" fill="white" />
                )}
              </div>
              <h2 className="text-3xl font-black mb-2 mt-4">
                {matchPopup.has_superlike ? "Super Match! ⭐" : "It's a Match!"}
              </h2>
              <p className="text-lg mb-4">You and {matchPopup.name} liked each other</p>
              {matchPopup.picture && (
                <img
                  src={matchPopup.picture}
                  alt={matchPopup.name}
                  className="w-24 h-24 mx-auto border-4 border-white object-cover mb-4"
                />
              )}
              <button
                onClick={() => setMatchPopup(null)}
                className="brutal-btn bg-white text-black"
                data-testid="match-continue-btn"
              >
                Keep Swiping
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SwipeCard({ profile, onSwipe }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-30, 30]);
  const likeOpacity = useTransform(x, [0, 100], [0, 1]);
  const passOpacity = useTransform(x, [-100, 0], [1, 0]);
  const superlikeOpacity = useTransform(y, [-100, 0], [1, 0]);

  const handleDragEnd = (_, info) => {
    if (info.offset.y < -100) {
      onSwipe("superlike");
    } else if (info.offset.x > 100) {
      onSwipe("like");
    } else if (info.offset.x < -100) {
      onSwipe("pass");
    }
  };

  const bgColors = ["bg-[#E3F4EA]", "bg-[#E8E6F8]", "bg-white"];

  return (
    <motion.div
      className={`swipe-card ${bgColors[Math.floor(Math.random() * bgColors.length)]}`}
      style={{ x, y, rotate }}
      drag
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      onDragEnd={handleDragEnd}
      data-testid="swipe-card"
    >
      {/* Like Indicator */}
      <motion.div className="swipe-like" style={{ opacity: likeOpacity }}>
        LIKE
      </motion.div>

      {/* Pass Indicator */}
      <motion.div className="swipe-pass" style={{ opacity: passOpacity }}>
        NOPE
      </motion.div>

      {/* Super Like Indicator */}
      <motion.div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[#FFD700] font-black text-4xl flex items-center gap-2 z-10"
        style={{ opacity: superlikeOpacity }}
      >
        <Star className="w-10 h-10" fill="currentColor" />
        SUPER LIKE
        <Star className="w-10 h-10" fill="currentColor" />
      </motion.div>

      {/* Profile Content */}
      <div className="h-full flex flex-col">
        {/* Photo */}
        <div className="flex-1 bg-gray-200 border-b-2 border-black overflow-hidden relative">
          {profile.picture || profile.photos?.[0] ? (
            <img
              src={profile.picture || profile.photos[0]}
              alt={profile.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-6xl font-black text-gray-400">
              {profile.name?.[0] || "?"}
            </div>
          )}
          
          {/* Online indicator */}
          {profile.is_online && (
            <div className="absolute top-4 right-4 flex items-center gap-2 bg-white px-3 py-1 border-2 border-black">
              <span className="w-3 h-3 bg-[#00CC99] rounded-full animate-pulse"></span>
              <span className="text-sm font-bold">Online</span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-4">
          <h2 className="text-2xl font-black">
            {profile.name}{profile.age ? `, ${profile.age}` : ""}
          </h2>
          {profile.location?.city && (
            <p className="flex items-center gap-1 text-[#4A4A4A] mb-2">
              <MapPin className="w-4 h-4" />
              {profile.location.city}
            </p>
          )}
          {profile.bio && (
            <p className="text-[#4A4A4A] line-clamp-2">{profile.bio}</p>
          )}
          {profile.interests?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {profile.interests.slice(0, 3).map((interest) => (
                <span
                  key={interest}
                  className="px-2 py-1 text-sm font-bold border-2 border-black bg-[#FFF5E1]"
                >
                  {interest}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
